// Mes Demandes de Devis - Cockpit Particulier
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Clock, CheckCircle, XCircle, MessageCircle, Plus,
  ChevronRight, ChevronDown, Loader2, X, ArrowLeft, MapPin,
  AlertCircle, RefreshCw
} from 'lucide-react';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'En attente', bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock, description: 'En attente de réponse des artisans' },
  received: { label: 'Devis reçus', bg: 'bg-blue-50', text: 'text-[#1E3A5F]', icon: CheckCircle, description: 'Des artisans ont répondu' },
  accepted: { label: 'Accepté', bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle, description: 'Devis accepté, chantier confirmé' },
  refused: { label: 'Refusé', bg: 'bg-red-50', text: 'text-red-600', icon: XCircle, description: 'Demande refusée' },
  expired: { label: 'Expiré', bg: 'bg-gray-100', text: 'text-gray-500', icon: XCircle, description: 'Demande expirée' },
};

const DEMO_DEVIS_REQUESTS = [
  {
    id: 'demo-req-1',
    prestation_name: 'Rénovation salle de bain complète',
    status: 'received',
    description: 'Rénovation complète : carrelage, plomberie, sanitaires et électricité',
    quantity: 8,
    quantity_unit: 'm²',
    estimated_price: 4500,
    city: 'Paris 15e',
    postal_code: '75015',
    preferred_date: 'Juillet 2025',
    created_at: new Date(Date.now() - 3 * 86400000).toISOString(),
    responses_count: 3,
    lowest_price: 3800,
    responses: [
      { id: 'r1', artisan_name: 'Plomberie Durand', artisan_id: 'a1', price: 3800, message: 'Devis détaillé fourni, disponible mi-juillet.', created_at: new Date(Date.now() - 2 * 86400000).toISOString(), validity_days: 30 },
      { id: 'r2', artisan_name: 'Réno Express', artisan_id: 'a2', price: 4200, message: 'Pose complète en 5 jours garantie.', created_at: new Date(Date.now() - 1 * 86400000).toISOString(), validity_days: 15 },
    ],
  },
  {
    id: 'demo-req-2',
    prestation_name: 'Installation climatisation réversible',
    status: 'pending',
    description: 'Climatisation réversible pour salon de 35m²',
    quantity: 1,
    quantity_unit: 'unité',
    estimated_price: 2000,
    city: 'Versailles',
    postal_code: '78000',
    created_at: new Date(Date.now() - 7 * 86400000).toISOString(),
    responses_count: 0,
    responses: [],
  },
  {
    id: 'demo-req-3',
    prestation_name: 'Peinture appartement 80m²',
    status: 'accepted',
    description: 'Peinture murs et plafonds, 4 pièces',
    quantity: 80,
    quantity_unit: 'm²',
    estimated_price: 2800,
    city: 'Boulogne-Billancourt',
    postal_code: '92100',
    created_at: new Date(Date.now() - 14 * 86400000).toISOString(),
    responses_count: 2,
    lowest_price: 2400,
    accepted_response_id: 'r3',
    responses: [
      { id: 'r3', artisan_name: 'Peinture Leblanc', artisan_id: 'a3', price: 2400, message: 'Travail soigné, finitions impeccables garanties.', created_at: new Date(Date.now() - 12 * 86400000).toISOString(), validity_days: 30 },
    ],
  },
];

function DevisDetailModal({ isOpen, onClose, devis, onAcceptResponse, onContact }) {
  const [isMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  const scrollRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      setShowScrollHint(scrollTop < scrollHeight - clientHeight - 20);
    }
  };

  if (!isOpen || !devis) return null;

  const status = STATUS_CONFIG[devis.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;

  const modalContent = (
    <>
      {/* Statut */}
      <div className={`flex items-center gap-3 p-3 rounded-xl ${status.bg}`}>
        <StatusIcon size={20} className={status.text} />
        <div>
          <p className={`font-semibold text-sm ${status.text}`}>{status.label}</p>
          <p className="text-xs text-gray-600">{status.description}</p>
        </div>
      </div>

      {/* Détails */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <h3 className="font-semibold text-gray-900 text-sm">Détails de la demande</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-gray-400 text-xs mb-0.5">Quantité</p>
            <p className="font-medium text-gray-900">{devis.quantity} {devis.quantity_unit}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-0.5">Estimation initiale</p>
            <p className="font-medium text-gray-900">{devis.estimated_price?.toLocaleString('fr-FR')} €</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-0.5">Localisation</p>
            <p className="font-medium text-gray-900">{devis.city} ({devis.postal_code})</p>
          </div>
          {devis.preferred_date && (
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Date souhaitée</p>
              <p className="font-medium text-gray-900">{devis.preferred_date}</p>
            </div>
          )}
        </div>
        {devis.description && (
          <div>
            <p className="text-gray-400 text-xs mb-1">Description</p>
            <p className="text-sm text-gray-700">{devis.description}</p>
          </div>
        )}
      </div>

      {/* Réponses des artisans */}
      <div>
        <h3 className="font-semibold text-gray-900 text-sm mb-3">
          Réponses reçues ({devis.responses_count || 0})
        </h3>
        {devis.responses && devis.responses.length > 0 ? (
          <div className="space-y-3">
            {devis.responses.map((response, index) => (
              <div
                key={response.id || index}
                className={`bg-white border rounded-xl p-4 ${
                  devis.accepted_response_id === response.id
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {response.artisan_name?.charAt(0) || 'A'}
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900">{response.artisan_name}</p>
                      <p className="text-xs text-gray-400">
                        Reçu le {new Date(response.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-[#1E3A5F]">{response.price?.toLocaleString('fr-FR')} €</p>
                    <p className="text-xs text-gray-400">Valide {response.validity_days || 30}j</p>
                  </div>
                </div>
                {response.message && (
                  <p className="text-sm text-gray-600 mb-3 bg-gray-50 p-2.5 rounded-lg italic">
                    "{response.message}"
                  </p>
                )}
                {devis.accepted_response_id === response.id ? (
                  <div className="flex items-center gap-2 text-green-600 text-sm font-semibold">
                    <CheckCircle size={16} />
                    Devis accepté
                  </div>
                ) : devis.status !== 'accepted' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onAcceptResponse(devis.id, response.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white transition-colors shadow-sm shadow-orange-500/25"
                    >
                      <CheckCircle size={14} />
                      Accepter ce devis
                    </button>
                    <button
                      onClick={() => onContact(response.artisan_id, response.artisan_name)}
                      className="px-3 py-2 rounded-xl border border-gray-200 text-gray-600 hover:border-orange-200 hover:text-orange-600 transition-colors"
                    >
                      <MessageCircle size={14} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-xl">
            <Clock size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500 text-sm">Aucune réponse pour le moment</p>
            <p className="text-gray-400 text-xs">Les artisans vont bientôt vous répondre</p>
          </div>
        )}
      </div>
    </>
  );

  // MOBILE — plein écran, slide-up
  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
          className="fixed inset-0 z-[10002] bg-white flex flex-col"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          {/* Header mobile */}
          <div className="flex items-center gap-3 px-4 border-b border-gray-100 flex-shrink-0"
            style={{ paddingTop: '12px', paddingBottom: '12px' }}>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft size={22} className="text-gray-500" />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="text-[15px] font-bold text-gray-900 truncate">{devis.prestation_name}</h2>
              <p className="text-xs text-gray-400">#{devis.id?.slice(0, 8)} • {new Date(devis.created_at).toLocaleDateString('fr-FR')}</p>
            </div>
          </div>
          {/* Content + scroll hint */}
          <div className="relative flex-1 overflow-hidden">
            <div ref={scrollRef} onScroll={handleScroll} className="h-full overflow-y-auto p-5 space-y-4">
              {modalContent}
            </div>
            {/* Gradient + indicateur de scroll */}
            <AnimatePresence>
              {showScrollHint && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none flex flex-col items-center justify-end pb-3"
                  style={{ background: 'linear-gradient(to top, white 30%, rgba(255,255,255,0.8) 60%, transparent 100%)' }}
                >
                  <motion.div
                    animate={{ y: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.1, ease: 'easeInOut' }}
                    className="w-7 h-7 rounded-full bg-orange-500 shadow-lg shadow-orange-500/40 flex items-center justify-center"
                  >
                    <ChevronDown size={14} className="text-white" />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* Footer */}
          <div className="px-5 border-t border-gray-100 flex gap-2 flex-shrink-0"
            style={{ paddingTop: '16px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}>
            <button onClick={onClose}
              className="flex-1 h-12 rounded-xl border border-gray-300 text-[#1e3a5f] text-sm font-medium hover:bg-gray-50 transition-colors">
              Fermer
            </button>
            {devis.status === 'pending' && (
              <button onClick={() => toast.info('Notification envoyée aux artisans')}
                className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2">
                <RefreshCw size={15} />
                Relancer
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  }

  // DESKTOP — dialog centré
  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10002] bg-black/60 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
            <div>
              <h2 className="text-base font-bold text-gray-900">{devis.prestation_name}</h2>
              <p className="text-xs text-gray-400">#{devis.id?.slice(0, 8)} • {new Date(devis.created_at).toLocaleDateString('fr-FR')}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {modalContent}
          </div>
          <div className="px-5 py-4 border-t border-gray-100 flex gap-2 flex-shrink-0">
            <button onClick={onClose}
              className="flex-1 h-12 rounded-xl border border-gray-300 text-[#1e3a5f] text-sm font-medium hover:bg-gray-50 transition-colors">
              Fermer
            </button>
            {devis.status === 'pending' && (
              <button onClick={() => toast.info('Notification envoyée aux artisans')}
                className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2">
                <RefreshCw size={15} />
                Relancer les artisans
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function DevisCard({ devis, onClick }) {
  const status = STATUS_CONFIG[devis.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-orange-200 hover:shadow-md transition-all cursor-pointer"
      onClick={onClick}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">{devis.prestation_name}</h3>
            <p className="text-xs text-gray-400">
              #{devis.id?.slice(0, 8)} • {new Date(devis.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 ${status.bg} ${status.text} flex-shrink-0 ml-2`}>
            <StatusIcon size={11} />
            {status.label}
          </span>
        </div>

        {devis.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{devis.description}</p>
        )}

        <div className="flex items-center gap-4 text-sm flex-wrap">
          <span className="text-gray-500">
            <span className="font-medium text-gray-900">{devis.responses_count || 0}</span> réponse{(devis.responses_count || 0) > 1 ? 's' : ''}
          </span>
          {devis.lowest_price && (
            <span className="text-gray-500">
              À partir de <span className="font-bold text-[#1E3A5F]">{devis.lowest_price?.toLocaleString('fr-FR')} €</span>
            </span>
          )}
          <span className="text-gray-400 text-xs flex items-center gap-1">
            <MapPin size={12} />
            {devis.city}
          </span>
        </div>
      </div>

      {devis.responses && devis.responses.length > 0 && (
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center gap-3 overflow-x-auto pb-0.5">
            {devis.responses.slice(0, 3).map((response, i) => (
              <div key={i} className="flex items-center gap-2 flex-shrink-0">
                <div className="w-8 h-8 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {response.artisan_name?.charAt(0) || 'A'}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-900 truncate max-w-[90px]">{response.artisan_name}</p>
                  <p className="text-xs text-[#1E3A5F] font-bold">{response.price?.toLocaleString('fr-FR')} €</p>
                </div>
              </div>
            ))}
            {devis.responses.length > 3 && (
              <span className="text-xs text-gray-400 flex-shrink-0">+{devis.responses.length - 3} autres</span>
            )}
          </div>
        </div>
      )}

      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-400">
          {devis.quantity} {devis.quantity_unit} • ~{devis.estimated_price?.toLocaleString('fr-FR')} €
        </span>
        <span className="text-xs text-orange-500 font-medium flex items-center gap-1">
          Voir détails <ChevronRight size={14} />
        </span>
      </div>
    </motion.div>
  );
}

function DevisArtisans() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [devis, setDevis] = useState(DEMO_DEVIS_REQUESTS);
  const [stats, setStats] = useState({ total: 3, pending: 1, received: 1, accepted: 1 });
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(true);
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const fetchDevisRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');

      const response = await fetch(`${API_URL}/api/artisans/my-devis-requests?status=${filter === 'all' ? '' : filter}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        const real = data.devis_requests || [];
        if (real.length > 0) {
          setDevis(real);
          setStats(data.stats || { total: 0, pending: 0, received: 0, accepted: 0 });
          setIsDemo(false);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevisRequests();
  }, [filter]);

  const handleAcceptResponse = async (requestId, responseId) => {
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/artisans/devis-requests/${requestId}/accept/${responseId}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        toast.success('Devis accepté ! L\'artisan va vous contacter.');
        setShowDetailModal(false);
        fetchDevisRequests();
      } else {
        toast.error('Erreur lors de l\'acceptation du devis');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const handleContact = (artisanId, artisanName) => {
    setShowDetailModal(false);
    setSelectedDevis(null);
    navigate(`/cockpit-particulier/messagerie?to=${artisanId}&name=${encodeURIComponent(artisanName)}`);
  };

  const handleOpenDetail = async (devisItem) => {
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/artisans/devis-requests/${devisItem.id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedDevis(data.devis_request);
      } else {
        setSelectedDevis(devisItem);
      }
    } catch {
      setSelectedDevis(devisItem);
    }
    setShowDetailModal(true);
  };

  const filters = [
    { id: 'all', label: 'Toutes', icon: null },
    { id: 'pending', label: 'En attente', icon: Clock },
    { id: 'received', label: 'Devis reçus', icon: CheckCircle },
    { id: 'accepted', label: 'Acceptées', icon: CheckCircle },
  ];

  const filteredDevis = filter === 'all' ? devis : devis.filter(d => d.status === filter);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Demandes de Devis</h1>
          <p className="text-sm text-gray-500">
            {loading ? '…' : `${stats.total || devis.length} demande${(stats.total || devis.length) > 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isDemo && (
            <span className="px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-lg text-xs font-medium text-amber-700">
              Aperçu démo
            </span>
          )}
          <button
            onClick={() => navigate('/decrire-projet')}
            className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-orange-500/25"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Demande de devis</span>
            <span className="sm:hidden">Devis</span>
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-3">
        <div className="sm:hidden">
          <div className="relative flex items-center">
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex gap-1.5 overflow-x-auto flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {filters.map(f => {
                const count = f.id === 'all' ? devis.length : devis.filter(d => d.status === f.id).length;
                const FilterIcon = f.icon;
                return (
                  <button key={f.id} onClick={() => setFilter(f.id)}
                    className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      filter === f.id ? 'bg-orange-500 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {FilterIcon && <FilterIcon size={12} />}
                    {f.label}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${filter === f.id ? 'bg-white/20' : 'bg-gray-100'}`}>{count}</span>
                  </button>
                );
              })}
            </div>
            {showScrollHint && (
              <div className="flex-shrink-0 flex items-center pointer-events-none">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>
        <div className="hidden sm:flex flex-wrap gap-2">
          {filters.map(f => {
            const count = f.id === 'all' ? devis.length : devis.filter(d => d.status === f.id).length;
            const FilterIcon = f.icon;
            return (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                  filter === f.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {FilterIcon && <FilterIcon size={14} />}
                {f.label}
                <span className={`px-1.5 py-0.5 rounded text-xs ${filter === f.id ? 'bg-white/20' : 'bg-gray-200'}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin text-orange-500" />
        </div>
      ) : filteredDevis.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {filter === 'all' ? 'Aucune demande de devis' : `Aucune demande "${filters.find(f => f.id === filter)?.label}"`}
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            {filter === 'all' ? 'Tu n\'as pas encore fait de demande de devis' : 'Aucune demande ne correspond à ce filtre'}
          </p>
          <button
            onClick={() => navigate('/decrire-projet')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            <Plus size={16} />
            Demander des devis
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDevis.map(d => (
            <DevisCard key={d.id} devis={d} onClick={() => handleOpenDetail(d)} />
          ))}
        </div>
      )}

      <DevisDetailModal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedDevis(null); }}
        devis={selectedDevis}
        onAcceptResponse={handleAcceptResponse}
        onContact={handleContact}
      />
    </div>
  );
}

export default DevisArtisans;
